import {
  Fade,
  Popper as MuiPopper,
  PopperPlacementType,
  useTheme,
} from '@mui/material';
import { ReactNode } from 'react';

import { SlideToolbar } from '@/modules/slideEditor/SlideToolbar';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { zIndexes } from '@/styles/zIndexes';

interface PresentationRowHoverToolbarProps {
  /**
   * The hovered element the toolbar anchors against. Usually the row's DOM
   * element; also an SVG element for chart-band hovers (e.g. the
   * household-overview beneficiary bars). MUI's `Popper` anchors to either via
   * `getBoundingClientRect`.
   */
  anchorEl: HTMLElement | SVGElement;
  /** The toolbar contents (action buttons). */
  children: ReactNode;
  /**
   * Spread the `toolbarProps` from `usePresentationRowHover` so moving
   * the cursor onto the toolbar keeps it open.
   */
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  /** Defaults to the row's top-left corner. */
  placement?: PopperPlacementType;
}

/**
 * Floating toolbar rendered against a hovered presentation row. Uses the
 * shared `SlideToolbar` container (the same chrome as the rich-text and
 * title-text editor toolbars) wrapped in `LuminaryThemeProvider` so the
 * editing chrome stays Luminary-navy over customer-themed slide content.
 * Mirrors the editor toolbars' positioning via MUI's `Popper`.
 */
export function PresentationRowHoverToolbar({
  anchorEl,
  children,
  onMouseEnter,
  onMouseLeave,
  placement = 'top-start',
}: PresentationRowHoverToolbarProps) {
  const theme = useTheme();
  return (
    <MuiPopper
      open
      transition
      anchorEl={anchorEl}
      placement={placement}
      // Small offset: large enough to read as floating above the row,
      // small enough that the cursor crosses into the toolbar before the
      // leave-debounce timer fires.
      modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
      sx={{ zIndex: zIndexes.POPOVER }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {({ TransitionProps }) => (
        // Brief ease-in fade so the toolbar appears rather than snapping in.
        <Fade
          {...TransitionProps}
          timeout={theme.transitions.duration.shortest}
          easing={{ enter: 'ease-in' }}
        >
          {/* The toolbar renders in a portal but is a React child of the
              hovered node/row, so clicks bubble through the React tree to the
              node's click handler (e.g. opening a diagram summary panel).
              Stop them here — toolbar actions should never select the node. */}
          <div onClick={(event) => event.stopPropagation()}>
            <LuminaryThemeProvider>
              <SlideToolbar>{children}</SlideToolbar>
            </LuminaryThemeProvider>
          </div>
        </Fade>
      )}
    </MuiPopper>
  );
}
